import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, Check, AlertCircle, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * /team/invite/:token — accept page for a team invite link emailed via Resend.
 * Flow: look up invite → if not logged in, redirect to /?next=/team/invite/:token → accept → /dashboard.
 */
export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/team/invite/lookup/${token}`);
        if (!cancelled) setInvite(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Invite not found");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post(`/team/invite/${token}/accept`);
      // Make the newly-joined business active
      localStorage.setItem("iti.activeBizId", data.business_id);
      toast.success(`You're a ${data.role} on this business`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't accept invite");
    }
    setAccepting(false);
  };

  if (loading || authLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FFFDF0]"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }
  if (error || !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFDF0]" data-testid="invite-not-found">
        <div className="nb-card p-8 max-w-md text-center">
          <AlertCircle className="w-10 h-10 mx-auto mb-3 text-red-600" />
          <div className="font-display text-2xl font-extrabold mb-2">Invite not available</div>
          <p className="text-gray-600 text-sm">{error || "This invite no longer exists."}</p>
          <Link to="/" className="inline-block mt-5 px-4 py-2 rounded-xl border-2 border-black font-semibold bg-white text-sm">Go home</Link>
        </div>
      </div>
    );
  }
  if (!invite.is_acceptable) {
    const reasonText = {
      accepted: "This invite has already been accepted.",
      revoked: "This invite was revoked by the owner.",
      expired: "This invite has expired. Ask the owner to send a new one.",
    }[invite.reason] || "This invite is no longer valid.";
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFDF0]" data-testid="invite-not-acceptable">
        <div className="nb-card p-8 max-w-md text-center">
          <AlertCircle className="w-10 h-10 mx-auto mb-3 text-amber-600" />
          <div className="font-display text-2xl font-extrabold mb-2">Hmm, this invite isn&apos;t ready</div>
          <p className="text-gray-600 text-sm">{reasonText}</p>
          <Link to="/" className="inline-block mt-5 px-4 py-2 rounded-xl border-2 border-black font-semibold bg-white text-sm">Go home</Link>
        </div>
      </div>
    );
  }
  if (!user) {
    // Send to landing with a redirect param so post-login we land back here.
    const next = encodeURIComponent(`/team/invite/${token}`);
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFDF0]" data-testid="invite-needs-signin">
        <div className="nb-card p-8 max-w-md text-center">
          <Users className="w-10 h-10 mx-auto mb-3 text-[#8B5CF6]" />
          <div className="font-display text-2xl font-extrabold mb-1">You&apos;re invited!</div>
          <p className="text-gray-700 mb-2">
            <b>{invite.owner_name}</b> invited you to manage <b>{invite.business_name}</b> as a <b>{invite.role}</b>.
          </p>
          <p className="text-sm text-gray-600 mb-5">Sign in with <b>{invite.email}</b> to accept.</p>
          <Link to={`/?next=${next}`} className="inline-block px-5 py-3 rounded-xl border-2 border-black font-bold bg-[#8B5CF6] text-white">Sign in to continue</Link>
        </div>
      </div>
    );
  }
  // Logged in — show accept screen
  const emailMatches = user.email.toLowerCase() === (invite.email || "").toLowerCase();
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFFDF0]" data-testid="invite-accept-screen">
      <div className="nb-card p-8 max-w-md w-full">
        <Users className="w-10 h-10 mb-3 text-[#8B5CF6]" />
        <div className="font-display text-3xl font-extrabold mb-1">Join {invite.business_name}</div>
        <p className="text-gray-700 mb-1">
          <b>{invite.owner_name}</b> invited <b>{invite.email}</b> to help manage this business as a <b>{invite.role}</b>.
        </p>
        {!emailMatches && (
          <div className="mt-4 nb-card-flat bg-[#FFF7E6] p-3 text-sm">
            You&apos;re signed in as <b>{user.email}</b>. The invite was sent to <b>{invite.email}</b>. Sign in with that email to accept.
          </div>
        )}
        <div className="mt-6 text-sm text-gray-700">
          <div className="font-bold mb-1">What you&apos;ll be able to do as a {invite.role}:</div>
          <ul className="list-disc ml-5 text-gray-600 space-y-0.5">
            <li>Edit website content and pages</li>
            <li>Update business settings</li>
            <li>Switch between your own businesses and this one</li>
          </ul>
        </div>
        <button
          onClick={accept}
          disabled={accepting || !emailMatches}
          className="w-full mt-6 px-5 py-3 rounded-xl border-2 border-black font-bold bg-[#10B981] text-white disabled:opacity-50 disabled:bg-gray-300 disabled:text-gray-700"
          data-testid="invite-accept-btn"
        >
          {accepting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <span className="flex items-center justify-center gap-2"><Check className="w-4 h-4" /> Accept invitation</span>}
        </button>
        <Link to="/dashboard" className="block text-center mt-3 text-sm text-gray-600 hover:underline">Not now, take me to my dashboard</Link>
      </div>
    </div>
  );
}
